import { useEffect, useState, type ReactNode } from 'react';
import { STUFE_INFO, STUFEN, WEBSITE_PUNKTE, WEGE, haeltAn, type Stufe } from '../../../shared/akquise';
import { api } from '../../api';
import { aktion } from '../../daten';
import { Detailfenster } from '../../komponenten/Detailfenster';
import { Symbol, useHinweis } from '../../komponenten/Grundbausteine';
import { gehe } from '../../ort';
import type { FirmaDetail, Zeile } from './typen';

/** Text der Stufe wie im Prototyp (stageText). */
export function stufenText(f: Zeile): string {
  if (f.stufe === 'anr') return `Anreicherung: ${STUFEN[f.schritt - 1] ?? STUFEN[0]}`;
  if (f.stufe === 'pruef') return `Prüfen: ${f.pruefung}`;
  if (f.stufe === 'kamp') return `${f.angebot}: ${f.angebotStatus}`;
  if (f.stufe === 'ende') return f.ende ?? STUFE_INFO.ende.label;
  return STUFE_INFO[f.stufe as Stufe].label;
}

const zeit = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const datum = (iso: string) => { const d = new Date(iso); return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`; };
const TON_URTEIL: Record<string, string> = { anbieten: 'ok', 'nicht anbieten': 'neutral', unsicher: 'warn' };
const ohneProtokoll = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

function Fakt({ d, children }: { d: string; children: ReactNode }) {
  return <><dt>{d}</dt><dd>{children}</dd></>;
}
const Fehlt = () => <span className="meta">nicht gefunden</span>;

export function FirmaFenster({ fid, schliessen, geaendert }: { fid: string | null; schliessen: () => void; geaendert: () => void }) {
  const [f, setF] = useState<FirmaDetail | null>(null);
  const [fehler, setFehler] = useState('');
  const hinweis = useHinweis();
  const [webNeu, setWebNeu] = useState<string | null>(null);
  const laden = (id: string) => api<FirmaDetail>(`/api/akquise/firmen/${id}`).then(d => { setF(d); setFehler(''); }, e => setFehler((e as Error).message));
  useEffect(() => { if (fid) laden(fid); }, [fid]);

  const ausfuehren = async (name: string, daten: unknown, text: string | ((e: Record<string, unknown>) => string)) => {
    try {
      const e = await aktion(name, daten);
      hinweis(typeof text === 'string' ? text : text(e));
      geaendert();
      if (fid) await laden(fid);
    } catch (e) {
      hinweis(`Geht nicht: ${(e as Error).message}`);
      if (fid) await laden(fid);
    }
  };

  const offen = Boolean(fid) && Boolean(f);
  if (!f) return fehler ? <Detailfenster offen={Boolean(fid)} schliessen={schliessen} kopf={<h2 id="drTitle">Firma</h2>}><p className="meta">{fehler}</p></Detailfenster> : null;

  const L = f.liste, info = STUFE_INFO[f.stufe as Stufe];
  const pruefHaelt = haeltAn(f.pruefung);
  const schritte = STUFEN.map((l, i) => {
    const m = f.marker[i], jetzt = f.stufe === 'anr' && f.schritt === i + 1;
    const att = (!m && Boolean(f.pruefung) && pruefHaelt && i >= 1) || (f.pruefung === 'Kategorie und Förderung' && i === 0);
    const cls = jetzt && f.listeLaeuft ? 'now' : att ? 'att' : m ? (m.startsWith('nein') || m.startsWith('nicht nötig') || m.startsWith('nicht beauftragt') ? 'todo' : 'done') : 'todo';
    const text = jetzt ? (f.listeLaeuft ? 'läuft' : 'wartet, Anreicherung der Liste gestoppt') : att && !m ? `wartet auf Prüfung (${f.pruefung})` : m || 'wartet';
    return { l, i, cls, text, m };
  });
  const bewertung = f.marker[4];
  const wb = f.bewertungWebsite, geltend = wb && !wb.wartet ? wb.leon ?? wb.routine : null;
  const angebotZeile = (o: FirmaDetail['angebote'][number]) => {
    const andere = f.angebote.find(b => b.angebot !== o.angebot && b.status);
    const opts = f.kampagnen.filter(k => k.angebot === o.angebot);
    const ton = o.status && ['Termin', 'in Kontakt'].includes(o.status) ? 'ok' : o.status && ['kein Interesse', 'keine Rückmeldung'].includes(o.status) ? 'neutral' : 'accent';
    return (
      <div className="offer" key={o.angebot}>
        <div className="offer-head"><strong>{o.angebot}</strong>{o.status ? <span className={`state ${ton}`}>{o.status}</span> : <span className="meta">noch nicht angesprochen</span>}</div>
        {o.kampagne && <p className="meta">Kampagne „{o.kampagne}“</p>}
        {!o.status && <>
          <label className="fld"><span className="meta">Entscheidung · Vorschlag aus der Kategorie: {f.vorschlag[o.angebot]}</span>
            <select className="input plain" data-offer={o.angebot} value={o.entscheidung === 'später' || o.entscheidung === 'nein' ? o.entscheidung : ''} onChange={e => {
              const v = e.target.value;
              if (v.startsWith('camp:')) {
                const k = opts.find(x => String(x.id) === v.slice(5));
                ausfuehren('zuordnen', { firmen: [f.fid], kampagne: Number(v.slice(5)) }, r => Number(r.zugeordnet) ? `„${f.name}“ der Kampagne „${k?.name}“ zugeordnet.` : `Geht nicht: ${Object.keys(r.uebersprungen as object)[0] ?? 'nicht möglich'}.`);
              } else ausfuehren('entscheidung', { firma: f.fid, angebot: o.angebot, wert: v || 'offen' }, v ? `${o.angebot}: „${v}“ gespeichert.` : `${o.angebot}: Entscheidung zurückgesetzt.`);
            }}>
              <option value="">offen</option>
              {opts.map(k => <option key={k.id} value={`camp:${k.id}`}>In Kampagne: {k.name}</option>)}
              <option value="später">später</option>
              <option value="nein">nein, nicht ansprechen</option>
            </select>
          </label>
          {andere && <span className="hint">Läuft schon für {andere.angebot}; höchstens ein Angebot gleichzeitig.</span>}
        </>}
      </div>
    );
  };

  return (
    <Detailfenster offen={offen} schliessen={schliessen}
      kopf={<>
        <span className="meta num">{f.fid}{L ? ` · ${L.land} › ${L.kat} › ${L.name}` : ''}</span>
        <h2 id="drTitle">{f.name}</h2>
        <span className={`state ${info.ton}`}>{stufenText(f)}</span>
      </>}
      fuss={f.stufe === 'kamp' ? <button className="btn" onClick={() => ausfuehren('ins_crm', { firma: f.fid }, `„${f.name}“ ins CRM übertragen.`)}>Ins CRM übertragen</button> : undefined}>
      {f.stufe === 'pruef' && (
        <div className="callout warn"><Symbol id="alert" /><div><strong>Prüfung offen: {f.pruefung}</strong>
          <span className="meta">{pruefHaelt ? 'Die Firma wird erst angesprochen, wenn die Prüfung entschieden ist.' : 'Bremst nichts; die Entscheidung verbessert nur die Angaben.'}</span>
          <button className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => { schliessen(); gehe('/akquise/pruefen'); }}>Zur Prüfung</button></div></div>
      )}
      <section><h3>Firma</h3><dl className="facts">
        <Fakt d="Firmenname">{f.name}{f.firmennameVorher && <span className="meta"> · aus dem Impressum, vorher „{f.firmennameVorher}“</span>}</Fakt>
        <Fakt d="Kategorie">{L?.name ?? <span className="meta">noch nicht eingeordnet</span>}{L && <span className="meta"> · {L.kat} · eingeordnet nach {f.kategorieHerkunft === 'Claude je Firma' ? 'Firma (KI)' : f.kategorieHerkunft === 'manuell' ? 'Hand' : 'Branche'}</span>}</Fakt>
        <Fakt d="Branche (Quelle)">{f.branche ?? <Fehlt />}</Fakt>
        <Fakt d="Quelle">{f.quelle ?? <Fehlt />}{f.weitere && f.weitere !== f.quelle && <span className="meta"> · auch in {f.weitere}</span>}</Fakt>
        <Fakt d="Adresse">{f.strasse ? `${f.strasse}, ` : ''}{f.plz} {f.ort}{f.ortsteil && <> <span className="meta">({f.ortsteil})</span></>}</Fakt>
        <Fakt d="Bundesland">{f.bundesland ?? <Fehlt />}{f.landkreis && f.landkreis !== f.bundesland && <span className="meta"> · {f.landkreis}</span>}</Fakt>
        <Fakt d="Mitarbeiter">{f.ma ?? <Fehlt />}</Fakt>
        <Fakt d="Website">{f.websiteTyp === 'keine Website vorhanden' || !f.website
          ? webNeu === null ? <><span className="meta">keine Website vorhanden · Ansprache nur per Telefon</span> <button className="btn btn-sm" onClick={() => setWebNeu('')}>Website eintragen</button></>
            : <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}><input className="input plain" aria-label="Website" placeholder="www.beispiel.de" value={webNeu} onChange={e => setWebNeu(e.target.value)} style={{ width: 220 }} />
              <button className="btn btn-sm btn-primary" disabled={webNeu.trim().length < 4} onClick={() => ausfuehren('website_setzen', { firma: f.fid, website: webNeu.trim() }, e => String(e.text ?? 'Gespeichert.')).then(() => setWebNeu(null))}>Speichern</button>
              <button className="btn btn-sm" onClick={() => setWebNeu(null)}>Abbrechen</button><span className="meta">danach läuft die Anreicherung ab Impressum neu</span></span>
          : <>{ohneProtokoll(f.website)}<span className="meta"> · {f.websiteTyp}</span></>}</Fakt>
      </dl></section>

      <section><h3>Ansprechpartner und Kontakt</h3><dl className="facts">
        <Fakt d="Ansprechpartner">{f.ap ? <>{f.ap}<span className="meta"> · {f.position ?? 'Position unbekannt'}, aus dem Impressum</span></> : <Fehlt />}</Fakt>
        <Fakt d="Persönliche Mail">{f.emailPersoenlich ? <>{f.emailPersoenlich}<span className="meta"> · {f.mailStatus}</span></> : <span className="meta">{f.mailStatus ?? 'wird noch gesucht'}</span>}</Fakt>
        <Fakt d="Allgemeine Mail">{f.emailAllgemein ? <>{f.emailAllgemein}<span className="meta"> · aus dem Impressum</span></> : <Fehlt />}</Fakt>
        {f.emailGespraech.length > 0 && <Fakt d="Mail aus dem Gespräch">{f.emailGespraech.join(', ')}<span className="meta"> · nur Telefon und CRM, nicht für Kaltmails</span></Fakt>}
        <Fakt d="Telefon allgemein">{f.telefonAllgemein ? <><span className="num">{f.telefonAllgemein}</span><span className="meta"> · aus {f.quelle}</span></> : <Fehlt />}</Fakt>
        <Fakt d="Telefon direkt">{f.telefonDirekt ? <span className="num">{f.telefonDirekt}</span> : <span className="meta">keine</span>}</Fakt>
      </dl>
        <div className="ways"><span className="meta">Kontaktweg, in fester Reihenfolge; der erste vorhandene wird genutzt:</span><ol>
          {WEGE.map((w, i) => <li key={w} className={w === f.weg ? 'on' : f.wegeVorhanden[i] ? 'avail' : ''}>{w}{w === f.weg ? ' · gewählt' : f.wegeVorhanden[i] ? '' : ' · fehlt'}</li>)}
        </ol></div></section>

      <section><h3>Förderung</h3><dl className="facts">
        <Fakt d="Förderfähig">{f.foerder === null ? <span className="meta">wird noch geprüft</span> : <>
          {f.foerder === 'ja' ? <span className="state ok">ja</span> : f.foerder === 'nein' ? <span className="state neutral">nein</span> : <span className="state warn">prüfen</span>}
          {f.foerderGrund && <span className="meta"> · {f.foerderGrund}</span>}</>}</Fakt>
        <Fakt d="Förderquote">{f.quoteBestimmt && f.foerder === 'ja' && f.quote ? <><span className="num">{f.quote} %</span><span className="meta"> · {f.landkreis ? `${f.landkreis}, aus der PLZ` : 'Landkreis nicht vermerkt'}</span></> : <span className="meta">noch offen</span>}</Fakt>
      </dl></section>

      <section><h3>Website-Bewertung</h3><dl className="facts">
        {f.websiteTyp !== 'eigene Website' ? <Fakt d="Urteil"><span className="meta">{f.websiteTyp === 'keine Website vorhanden' ? 'keine eigene Website: Angebot für eine erste Website' : bewertung || 'nicht nötig'}</span></Fakt>
          : !wb || wb.wartet ? <Fakt d="Urteil"><span className="meta">{f.bewerten || wb ? 'wartet auf die Website-Routine' : bewertung || 'nicht beauftragt'}</span></Fakt>
          : <>
            <Fakt d="Urteil"><span className={`state ${TON_URTEIL[geltend!] ?? 'neutral'}`}>{geltend}</span>
              <span className="meta"> · {wb.leon ? `von dir${wb.leonAm ? ` am ${datum(wb.leonAm)}` : ''}` : `Website-Routine${wb.am ? ` am ${datum(wb.am)}` : ''}`}</span></Fakt>
            {wb.leon && wb.routine !== wb.leon && <Fakt d="Routine sagte">{wb.routine}</Fakt>}
            <Fakt d="Begründung">{wb.begruendung}</Fakt>
            {(wb.sichtbar === false || wb.gehoert === false) && <Fakt d="Hinweis"><span className="state warn">{wb.gehoert === false ? 'Seite gehört wohl nicht zur Firma' : 'Seite war nicht richtig zu sehen'}</span></Fakt>}
            {WEBSITE_PUNKTE.map(([k, l]) => wb.punkte?.[k] ? <Fakt key={k} d={l}><span className="meta">{wb.punkte?.[k]}</span></Fakt> : null)}
          </>}
      </dl>
        {f.websiteTyp === 'eigene Website' && !f.bewerten && !wb && <button className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => ausfuehren('website_bewerten', { firma: f.fid }, 'Website-Bewertung beauftragt; die Website-Routine urteilt beim nächsten Lauf.')}>Website bewerten lassen</button>}
        {wb && !wb.wartet && <div style={{ display: 'flex', gap: 'var(--s2)', flexWrap: 'wrap' }}>
          {(['anbieten', 'nicht anbieten'] as const).filter(u => u !== geltend).map(u => <button key={u} className="btn btn-sm" onClick={() => ausfuehren('website_urteil', { firma: f.fid, urteil: u }, `Auf „${u}“ gestellt; die Bewertung lernt das als Beispiel.`)}>Auf „{u}“ stellen</button>)}
        </div>}
        {geltend === 'unsicher' && <p className="hint">Liegt unter Prüfen bei dir. Bis du entscheidest, bekommt die Firma kein Website-Angebot; andere Angebote laufen weiter.</p>}
      </section>

      <section><h3>Anreicherung</h3><ol className="steps">
        {schritte.map(s => (
          <li key={s.l} className={s.cls}>
            <span className="dot">{s.cls === 'done' ? <Symbol id="check" /> : s.cls === 'att' ? <Symbol id="alert" /> : s.cls === 'now' ? <Symbol id="play" /> : null}</span>
            <span>{s.l}<span className="sub meta">{s.text}</span></span>
            {s.m && !s.m.startsWith('nicht nötig') && !s.m.startsWith('nicht beauftragt')
              ? <button className="btn btn-sm btn-ghost" onClick={() => ausfuehren('stufe_neu', { firma: f.fid, stufe: s.i + 1 }, `Stufe „${s.l}“ läuft für diese Firma neu; die Stufen danach ebenfalls.`)}>Neu</button>
              : <span />}
          </li>
        ))}
      </ol>
        <p className="hint">Ändert sich Adresse, Ansprechpartner oder Website, laufen die betroffenen Stufen von selbst neu.</p></section>

      <section><h3>Angebote</h3><div className="offers">{f.angebote.map(angebotZeile)}</div></section>

      <section><h3>Verlauf</h3><ul className="log">
        {f.verlauf.map((v, i) => <li key={i}><time>{zeit(v.zeit)}</time><span>{v.text}</span></li>)}
      </ul></section>
    </Detailfenster>
  );
}
